// Rails 8 Turbo and Stimulus Compatibility Layer
// This file ensures Rails 8 compatibility for Turbo and Stimulus

import { Turbo } from "@hotwired/turbo-rails"

// Ensure proper initialization for Rails 8
document.addEventListener("DOMContentLoaded", () => {
  console.log("Rails 8 Turbo compatibility initializing")
  
  // Ensure Turbo is globally available
  if (!window.Turbo) {
    window.Turbo = Turbo
  }
  
  // Configure Turbo Progress Bar
  if (window.Turbo && window.Turbo.setProgressBarDelay) {
    window.Turbo.setProgressBarDelay(0)
  }
  
  // Ensure navigation is enabled
  if (window.Turbo && window.Turbo.session) {
    window.Turbo.session.drive = true
  }
  
  // Enable Turbo cache (Rails 8 best practice)
  if (window.Turbo && window.Turbo.session && window.Turbo.session.cache) {
    window.Turbo.session.cache.enabled = true
    window.Turbo.session.cache.size = 20
  }
  
  // Register CSP nonce handler (required for Rails 8's enhanced CSP)
  document.addEventListener("turbo:before-fetch-request", (event) => {
    const nonce = document.querySelector('meta[name="csp-nonce"]')?.getAttribute('content')
    if (nonce) {
      event.detail.fetchOptions.headers["X-Turbo-Nonce"] = nonce
    }
  })
  
  // Handle form submissions properly in Rails 8
  document.addEventListener("turbo:submit-start", (event) => {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    // Add a loading indicator if needed
    if (form.dataset.turboLoading) {
      const submitButtons = form.querySelectorAll("button[type=submit], input[type=submit]")
      submitButtons.forEach(button => {
        if (button.dataset.turboLoadingText) {
          button.dataset.originalText = button.innerText
          button.innerText = button.dataset.turboLoadingText
        }
        button.disabled = true
      })
    }
  })
  
  // Reset form state after submission
  document.addEventListener("turbo:submit-end", (event) => {
    const form = event.target
    if (!(form instanceof HTMLFormElement)) return
    if (form.dataset.turboLoading) {
      const submitButtons = form.querySelectorAll("button[type=submit], input[type=submit]")
      submitButtons.forEach(button => {
        if (button.dataset.originalText) {
          button.innerText = button.dataset.originalText
          delete button.dataset.originalText
        }
        button.disabled = false
      })
    }
  })

  // Properly handle turbo:frame-missing in Rails 8
  document.addEventListener("turbo:frame-missing", (event) => {
    // If a frame is missing, we can navigate to the URL directly
    if (event.detail.response?.redirected) {
      event.preventDefault()
      window.location.href = event.detail.response.url
    }
  })
  
  // Register frame rendering callback for Rails 8 frames
  // Note: Stimulus automatically initializes controllers on new DOM elements via MutationObserver,
  // so no manual re-initialization is needed here. This event handler is kept for potential
  // future use or custom frame-render logic.
  document.addEventListener("turbo:frame-render", (event) => {
    // Stimulus handles controller initialization automatically when new elements are added to the DOM
  })
})

// Debug logging for Turbo Stream rendering
document.addEventListener("turbo:before-stream-render", (event) => {
  console.log("Turbo: Before stream render", event)
})

// Register better error handling for Rails 8.
// Registered once at load — previously this was added inside turbo:load, which
// stacked a new window listener on every navigation.
window.addEventListener("error", (event) => {
  console.error("Unhandled error in Turbo:", event.error)

  // If the error is related to Turbo, we can recover gracefully
  if (event.error && event.error.message && event.error.message.includes("Turbo")) {
    event.preventDefault()

    // Try to refresh the page if needed
    if (window.Turbo && window.Turbo.visit) {
      console.log("Attempting to recover from Turbo error by reloading the page")
      window.Turbo.clearCache()
      window.Turbo.visit(window.location.href, { action: "replace" })
    }
  }
})

// Enable debug mode in development
if (process.env.NODE_ENV === "development") {
  console.log("Rails 8 Turbo debug mode enabled")
  if (window.Turbo) {
    window.Turbo.debugMode = true
  }
  
  if (window.Stimulus) {
    window.Stimulus.debug = true
  }
}